import { useReducer } from "react";

import { MAX_STUDENT_ASSIGNEES_TO_LIST } from "../AssignmentEditor/AssignmentEditor";
import {
  ASSIGNMENT_VERSION_MVE,
  getFlattenedClassrooms,
  getTruncatedSmallGroupUserNames,
} from "../utils/assignmentUtils";
import { getHasAssignees } from "../utils/assignmentValidationHelpers/assignmentValidationHelpers";
import formatUpdatedActivities from "../utils/formatUpdatedActivities";
export const ASSIGNMENT_ACTIONS = {
  REMOVE_CLASSROOM_ASSIGNEE: "REMOVE_CLASSROOM_ASSIGNEE",
  REMOVE_STUDENT_ASSIGNEE: "REMOVE_STUDENT_ASSIGNEE",
  REMOVE_ACTIVITY_ELEMENT_SELECTIONS: "REMOVE_ACTIVITY_ELEMENT_SELECTIONS",
  SET_ACTIVITY_ELEMENT_SELECTIONS: "SET_ACTIVITY_ELEMENT_SELECTIONS",
  SET_ASSIGNMENT_INSTRUCTIONS: "SET_ASSIGNMENT_INSTRUCTIONS",
  SET_ASSIGNMENT_LANGUAGE: "SET_ASSIGNMENT_LANGUAGE",
  SET_ASSIGNMENT_TITLE: "SET_ASSIGNMENT_TITLE",
  SET_ASSIGNMENT: "SET_ASSIGNMENT",
  SET_CONSENT: "SET_CONSENT",
  SET_DEFAULT_READING_LEVEL: "SET_DEFAULT_READING_LEVEL",
  SET_IS_READING_LEVEL_LOCKED: "SET_IS_READING_LEVEL_LOCKED",
  SET_STUDENT_RESTRICTED_CONSENT: "SET_STUDENT_RESTRICTED_CONSENT",
  SET_SCHEDULED_DATE: "SET_SCHEDULED_DATE",
  UPDATE_ACTIVITY_SELECTIONS: "UPDATE_ACTIVITY_SELECTIONS",
  UPDATE_CLASSROOM_ASSIGNEES: "UPDATE_CLASSROOM_ASSIGNEES",
  UPDATE_STUDENT_ASSIGNEES: "UPDATE_STUDENT_ASSIGNEES",
  SET_ASSIGNMENT_CONFIG: "SET_ASSIGNMENT_CONFIG",
  SET_ASSIGNEE_ERROR: "SET_ASSIGNEE_ERROR",
  SET_HAS_UNSAVED_CHANGES: "SET_HAS_UNSAVED_CHANGES",
  RESET_ASSIGNMENT: "RESET_ASSIGNMENT",
  SET_ASSIGNMENT_MEMBER: "SET_ASSIGNMENT_MEMBER",
};

/**
 * For existing assignments, the initial state is set in useSetAssignmentOnLoad
 * @link @/assignments/utils/useSetAssignmentOnLoad
 */
export const DEFAULT_STATE = {
  activity_members: [],
  assignment_classrooms: [],
  assignment_groups: [],
  assignment_members: [],
  assignment_config: {
    hide_language_selector: false,
    hide_lexile_level: false,
    hide_read_aloud: false,
    hide_quiz_answers: false,
    disable_vocabulary_support: false,
    hide_checks_for_understanding: false,
    hide_generation_genius_discussion_answers: false,
    hide_generation_genius_vocab_definitions: false,
    hide_guided_highlighting: true,
    hide_translation: false,
    show_generation_genius_video_only: false,
  },
  content_id: null,
  copied_from_assignment: undefined,
  default_reading_level: null,
  grade_level_lock: null,
  instructions: "",
  is_draft: true,
  is_reading_level_locked: false,
  title: "",
  date_goes_live: null,
  date_modified: null,
  version: ASSIGNMENT_VERSION_MVE,
  can_edit_assignees: true,
  hasUnsavedChanges: false,
};

/* empty assignment state for non-teachers when there is no saved assignment available */
export const LEARNER_DEFAULT_ASSIGNMENT = {
  assignment_config: {
    ...DEFAULT_STATE.assignment_config,
    hide_checks_for_understanding: true,
  },
};

export const assignmentReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case ASSIGNMENT_ACTIONS.SET_ASSIGNMENT_TITLE:
      return {
        ...state,
        title: payload,
      };
    case ASSIGNMENT_ACTIONS.SET_ASSIGNMENT_INSTRUCTIONS:
      return {
        ...state,
        instructions: payload,
      };
    case ASSIGNMENT_ACTIONS.SET_ASSIGNMENT: {
      return {
        ...payload,
      };
    }
    case ASSIGNMENT_ACTIONS.SET_ASSIGNMENT_MEMBER:
      return {
        ...state,
        assignment_members: [
          {
            content_id: payload,
          },
        ],
      };
    case ASSIGNMENT_ACTIONS.SET_SCHEDULED_DATE:
      return {
        ...state,
        date_goes_live: payload,
      };
    case ASSIGNMENT_ACTIONS.SET_DEFAULT_READING_LEVEL:
      return {
        ...state,
        default_reading_level: payload,
      };
    case ASSIGNMENT_ACTIONS.SET_IS_READING_LEVEL_LOCKED:
      return {
        ...state,
        is_reading_level_locked: payload,
      };
    case ASSIGNMENT_ACTIONS.SET_STUDENT_RESTRICTED_CONSENT:
      return {
        ...state,
        content_settings_override: payload,
      };
    case ASSIGNMENT_ACTIONS.REMOVE_STUDENT_ASSIGNEE: {
      const {
        assignment_groups: [assignmentGroup],
      } = state;
      const studentAssigneesCopy = assignmentGroup.users.map(
        (studentAssignee) => ({
          ...studentAssignee,
        }),
      );
      const updatedStudentAssignees = studentAssigneesCopy.filter(
        (assignee) => {
          return assignee.unique_id !== payload;
        },
      );
      if (!updatedStudentAssignees.length) {
        return {
          ...state,
          assignment_groups: [],
        };
      }
      const updatedGroupName = getTruncatedSmallGroupUserNames(
        updatedStudentAssignees,
        MAX_STUDENT_ASSIGNEES_TO_LIST,
      );
      return {
        ...state,
        assignment_groups: [
          {
            ...assignmentGroup,
            name: updatedGroupName,
            users: updatedStudentAssignees,
          },
        ],
      };
    }
    case ASSIGNMENT_ACTIONS.UPDATE_STUDENT_ASSIGNEES: {
      // set new list of student assignees; unset list of classroom assignees
      if (!payload.length) {
        return {
          ...state,
          assignment_groups: [],
        };
      }
      const {
        assignment_groups: [assignmentGroup],
      } = state;
      const updatedGroupName = getTruncatedSmallGroupUserNames(
        payload,
        MAX_STUDENT_ASSIGNEES_TO_LIST,
      );
      return {
        ...state,
        assignment_groups: [
          {
            ...assignmentGroup,
            name: updatedGroupName,
            users: payload,
          },
        ],
        assignment_classrooms: [],
      };
    }

    case ASSIGNMENT_ACTIONS.REMOVE_CLASSROOM_ASSIGNEE: {
      const { assignment_classrooms: assignmentClassrooms } = state;
      const assigneeClassroomsCopy = assignmentClassrooms.map(
        (classAssignee) => ({
          ...classAssignee,
        }),
      );
      const updatedClassroomAssignees = assigneeClassroomsCopy.filter(
        (classAssignee) => classAssignee.unique_id !== payload,
      );
      return {
        ...state,
        assignment_classrooms: updatedClassroomAssignees,
      };
    }
    case ASSIGNMENT_ACTIONS.UPDATE_CLASSROOM_ASSIGNEES: {
      const flatAssignmentClassrooms = getFlattenedClassrooms(payload);

      // set new list of classroom assignees; unset list of student assignees
      return {
        ...state,
        assignment_classrooms: flatAssignmentClassrooms,
        assignment_groups: [],
      };
    }
    case ASSIGNMENT_ACTIONS.UPDATE_ACTIVITY_SELECTIONS: {
      return {
        ...state,
        activity_members: formatUpdatedActivities(
          state.activity_members,
          payload,
        ),
      };
    }
    case ASSIGNMENT_ACTIONS.SET_ASSIGNMENT_CONFIG: {
      return {
        ...state,
        assignment_config: {
          ...state.assignment_config,
          ...payload,
        },
      };
    }
    case ASSIGNMENT_ACTIONS.SET_ASSIGNEE_ERROR: {
      return {
        ...payload,
        errors: {
          noAssignees: !getHasAssignees(payload),
        },
      };
    }
    case ASSIGNMENT_ACTIONS.SET_HAS_UNSAVED_CHANGES: {
      return {
        ...state,
        hasUnsavedChanges: payload,
      };
    }
    case ASSIGNMENT_ACTIONS.SET_ACTIVITY_ELEMENT_SELECTIONS: {
      return {
        ...state,
        activity_element_selections: payload,
      };
    }
    case ASSIGNMENT_ACTIONS.REMOVE_ACTIVITY_ELEMENT_SELECTIONS: {
      const newState = { ...state };
      delete newState.activity_element_selections;
      return newState;
    }
    case ASSIGNMENT_ACTIONS.RESET_ASSIGNMENT: {
      const newState = { ...DEFAULT_STATE };
      return newState;
    }
    default:
      return state;
  }
};

const useAssignmentReducer = (initialState = DEFAULT_STATE) => {
  return useReducer(assignmentReducer, initialState);
};

export default useAssignmentReducer;
